import { useQuery } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Card, Metric, Loading, ErrorState, Button } from '../../components/ui.jsx';
import { money, number, percent } from '../../lib/format.js';

/** Hero-row glyphs, drawn inline like the rest of the panel's icons. */
const ICONS = {
  // Set as a glyph rather than a path: the money in this panel is rupees, and
  // the icon set's currency mark is a dollar.
  revenue: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <text x="12" y="18" textAnchor="middle" fontSize="18" fontWeight="600" fill="currentColor">
        ₹
      </text>
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  ),
  listings: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM9 17H7v-5h2v5zm4 0h-2V7h2v10zm4 0h-2v-8h2v8z" />
    </svg>
  ),
  leads: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.65 10A5.99 5.99 0 0 0 7 6a6 6 0 1 0 5.65 8H17v4h4v-4h2v-4H12.65zM7 14a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
    </svg>
  ),
  attention: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  ),
};

const rate = (part, whole) => (whole ? (part / whole) * 100 : 0);

/**
 * Platform overview.
 *
 * Rentals only. The stays side — bookings, hosts, nightly revenue and the
 * payout ledger behind it — is no longer part of what this panel oversees,
 * and a card of figures nobody acts on is worse than one less card.
 *
 * Every number here is one the API actually sends. The People card used to
 * list guests, hosts and tenants, none of which `/admin/dashboard` returns:
 * the endpoint counts the two populations records live in — customers and
 * brokers — so all three rows rendered as an em dash.
 */
export default function DashboardPage() {
  const query = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard, staleTime: 30_000 });

  if (query.isLoading) return <Page title="Dashboard"><Loading label="Loading platform metrics" /></Page>;
  if (query.isError) {
    return (
      <Page title="Dashboard">
        <ErrorState error={query.error} onRetry={query.refetch} />
      </Page>
    );
  }

  const data = query.data;

  /*
   * An older backend has no `creditSales` in its payload, and a dashboard
   * that throws is worse than one showing zero — so the shape is filled in
   * rather than assumed.
   */
  const ZERO_SALES = {
    grossMinor: 0,
    taxMinor: 0,
    refundedMinor: 0,
    netMinor: 0,
    exTaxMinor: 0,
    purchases: 0,
  };

  const credits = {
    ...ZERO_SALES,
    ...(data.creditSales || {}),
    last30Days: { ...ZERO_SALES, ...(data.creditSales?.last30Days || {}) },
  };

  const liveRentalRate = rate(data.brokerProperties.active, data.brokerProperties.total);
  const unlockRate = rate(data.leads.unlocked, data.leads.total);

  /*
   * An older backend sends no `enquiries` block, and a dashboard that throws
   * is worse than one showing zero.
   */
  const enquiries = {
    total: 0, unlocked: 0, waiting: 0, accepted: 0, converted: 0, declined: 0,
    ...(data.enquiries || {}),
  };
  const enquiryUnlockRate = rate(enquiries.unlocked, enquiries.total);
  const conversionRate = rate(enquiries.converted, enquiries.total);
  const needsAttention =
    (data.moderation?.openReports || 0) +
    (data.moderation?.pendingBrokerVerifications || 0) +
    (data.moderation?.pendingListingReviews || 0) +
    (data.moderation?.pendingIdentityReviews || 0);

  return (
    <Page
      title="Dashboard Overview"
      subtitle="Welcome back. Here is what is happening today."
      actions={
        <Button variant="outline" size="sm" onClick={() => query.refetch()} loading={query.isFetching}>
          Refresh
        </Button>
      }
    >
      <div className="metric-grid">
        <Metric
          label="Credit sales"
          value={money(credits.netMinor, { compact: true })}
          caption={`${money(credits.last30Days.netMinor, { compact: true })} in the last 30 days`}
          icon={ICONS.revenue}
        />
        <Metric
          label="Live rentals"
          value={number(data.brokerProperties.active)}
          caption={`${number(data.brokerProperties.total)} listed all time`}
          icon={ICONS.listings}
          progress={liveRentalRate}
        />
        <Metric
          label="Leads unlocked"
          value={number(data.leads.unlocked)}
          caption={`${number(data.leads.total)} generated`}
          icon={ICONS.leads}
        />
        <Metric
          label="Total users"
          value={number(data.users.total)}
          caption={`${number(data.users.brokers)} brokers`}
          icon={ICONS.users}
        />
        <Metric
          label="Needs attention"
          value={number(needsAttention)}
          caption="Open reports and pending verifications"
          icon={ICONS.attention}
          accent={needsAttention ? 'warning' : undefined}
        />
      </div>

      <div className="two-column">
        <div className="stack">
          <Card
            title="Credit sales"
            subtitle="Money taken for credit top-ups, after refunds. Credits granted at signup or by support are not sales at all."
          >
            <div className="metric-grid">
              <Metric
                label="Kept"
                value={money(credits.netMinor)}
                caption={`${number(credits.purchases)} purchase${credits.purchases === 1 ? '' : 's'}, after refunds`}
                accent="success"
              />
              <Metric label="Charged" value={money(credits.grossMinor)} caption="Everything taken at the till" />
              <Metric
                label="Refunded"
                value={money(credits.refundedMinor)}
                accent={credits.refundedMinor > 0 ? 'warning' : undefined}
              />
              <Metric
                label="Last 30 days"
                value={money(credits.last30Days.netMinor)}
                caption={`${number(credits.last30Days.purchases)} purchase${credits.last30Days.purchases === 1 ? '' : 's'}, after refunds`}
              />
            </div>
          </Card>

          <Card title="Rentals" subtitle="Long-term lettings, listed by agencies and owners">
            <div className="metric-grid">
              <Metric
                label="Live"
                value={number(data.brokerProperties.active)}
                caption={`${percent(liveRentalRate, 0)} of everything listed`}
              />
              <Metric label="Rented" value={number(data.brokerProperties.rented)} accent="success" />
              <Metric label="Listed all time" value={number(data.brokerProperties.total)} />
            </div>
          </Card>

          {/* The supply side, beside the demand side. An owner handing a
              flat to a broker is the mirror of a tenant's requirement
              reaching one, and the same two questions apply: how many, and
              how many did a broker think worth paying for. */}
          <Card
            title="Enquiries"
            subtitle="An owner handing a property to a broker they chose, unlocked with credits"
          >
            <div className="metric-grid">
              <Metric label="Sent" value={number(enquiries.total)} />
              <Metric
                label="Waiting on a broker"
                value={number(enquiries.waiting)}
                caption="Unopened, or opened and not yet answered"
                accent={enquiries.waiting ? 'warning' : undefined}
              />
              <Metric
                label="Unlocked"
                value={number(enquiries.unlocked)}
                caption={`${percent(enquiryUnlockRate, 1)} paid for`}
                accent="success"
              />
              <Metric label="Accepted" value={number(enquiries.accepted)} />
              <Metric
                label="Became listings"
                value={number(enquiries.converted)}
                caption={`${percent(conversionRate, 1)} of everything sent`}
                accent="success"
                progress={conversionRate}
              />
              <Metric
                label="Declined"
                value={number(enquiries.declined)}
                accent={enquiries.declined ? 'warning' : undefined}
              />
            </div>
          </Card>

          <Card title="Leads" subtitle="A tenant's requirement, matched to a broker and paid for in credits">
            <div className="metric-grid">
              <Metric label="Generated" value={number(data.leads.total)} />
              <Metric
                label="Unlocked"
                value={number(data.leads.unlocked)}
                caption="Paid for by a broker"
                accent="success"
              />
              <Metric
                label="Unlock rate"
                value={percent(unlockRate, 1)}
                caption="Share of leads a broker thought worth buying"
                progress={unlockRate}
              />
            </div>
          </Card>

          <Card
            title="Needs attention"
            subtitle="The queues that hold somebody up while they sit unread"
          >
            <div className="metric-grid">
              <Metric
                label="Open reports"
                value={number(data.moderation?.openReports || 0)}
                accent={data.moderation?.openReports ? 'warning' : undefined}
              />
              <Metric
                label="Brokers awaiting verification"
                value={number(data.moderation?.pendingBrokerVerifications || 0)}
                caption="Unverified agencies rank lower until this clears"
                accent={data.moderation?.pendingBrokerVerifications ? 'warning' : undefined}
              />
              <Metric
                label="Listings awaiting review"
                value={number(data.moderation?.pendingListingReviews || 0)}
                caption="Owner listings stay hidden until approved"
                accent={data.moderation?.pendingListingReviews ? 'warning' : undefined}
              />
              <Metric
                label="IDs awaiting review"
                value={number(data.moderation?.pendingIdentityReviews || 0)}
                caption="Users → ID waiting for review"
                accent={data.moderation?.pendingIdentityReviews ? 'warning' : undefined}
              />
            </div>
          </Card>
        </div>

        <Card title="People">
          <div className="stack stack--sm">
            <PeopleRow label="Total users" value={data.users.total} />
            {/* Customers and brokers are separate collections, which is why
                this split is a fact about where a record lives rather than a
                field somebody set. */}
            <PeopleRow label="Tenants and owners" value={data.users.customers} />
            <PeopleRow label="Brokers" value={data.users.brokers} />
          </div>
          <p className="muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
            One person can hold an account in both, so these need not sum to the total.
          </p>
        </Card>
      </div>
    </Page>
  );
}

function PeopleRow({ label, value }) {
  return (
    <div className="row row--between">
      <span className="muted" style={{ fontSize: 14 }}>
        {label}
      </span>
      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{number(value)}</strong>
    </div>
  );
}
